import React, { type ComponentProps, forwardRef } from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';
export type ButtonAppearance = 'solid' | 'outline' | 'ghost';
export type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';

export type ButtonProps = ComponentProps<'button'> & {
  /** Semantic variant: primary, secondary, or danger (maps to destructive in styles). */
  variant?: ButtonVariant;
  /** Visual style: solid, outline, or ghost. */
  appearance?: ButtonAppearance;
  /** When true, uses full border-radius for icon-only/pill shape. */
  circular?: boolean;
  /** When true, button uses inverse theme (e.g. on dark surface in light theme). */
  inverseTheme?: boolean;
  /** Size: lg, md, or sm. */
  size?: ButtonSize;
};

const variantToClass: Record<ButtonVariant, string> = {
  danger: 'str-chat__button--destructive',
  primary: 'str-chat__button--primary',
  secondary: 'str-chat__button--secondary',
};

const appearanceToClass: Record<ButtonAppearance, string> = {
  ghost: 'str-chat__button--ghost',
  outline: 'str-chat__button--outline',
  solid: 'str-chat__button--solid',
};

const sizeToClass: Record<ButtonSize, string> = {
  lg: 'str-chat__button--size-lg',
  md: 'str-chat__button--size-md',
  sm: 'str-chat__button--size-sm',
  xs: 'str-chat__button--size-xs',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { appearance, children, circular, className, inverseTheme, size, variant, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type='button'
      {...props}
      className={clsx(
        'str-chat__button',
        variant != null && variantToClass[variant],
        appearance != null && appearanceToClass[appearance],
        circular && 'str-chat__button--circular',
        inverseTheme && 'str-chat__theme-inverse',
        size != null && sizeToClass[size],
        className,
      )}
    >
      <div className='str-chat__button__content'>{children}</div>
    </button>
  );
});
